import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AlertOctagon, BellOff, BellRing, RefreshCw, Filter } from "lucide-react";

const TIME_WINDOWS = [
    { label: "Última hora", v: 1 },
    { label: "Últimas 24h", v: 24 },
    { label: "Últimos 7 días", v: 168 },
    { label: "Últimos 30 días", v: 720 },
];

export default function ErrorLogs() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [filters, setFilters] = useState({ signature: "", user_id: "", since_hours: 24, q: "" });
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 50;
    const [expanded, setExpanded] = useState(null);
    const [silencing, setSilencing] = useState(null);

    const load = async () => {
        setLoading(true); setError("");
        try {
            const params = { since_hours: filters.since_hours, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
            if (filters.signature) params.signature = filters.signature;
            if (filters.user_id) params.user_id = filters.user_id;
            const { data } = await api.get("/errors/logs", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) { setError(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filters.signature, filters.user_id, filters.since_hours, page]); // eslint-disable-line

    const filtered = useMemo(() => {
        if (!filters.q) return items;
        const q = filters.q.toLowerCase();
        return items.filter((r) => `${r.message} ${r.url} ${r.user_agent} ${r.user?.email || ""}`.toLowerCase().includes(q));
    }, [items, filters.q]);

    const silence = async (sig) => {
        setSilencing(sig); setOk(""); setError("");
        try {
            const { data } = await api.post(`/errors/signatures/${sig}/silence?hours=24`);
            setOk(`Firma ${sig} silenciada hasta ${new Date(data.silenced_until).toLocaleString("es-MX")}.`);
        } catch (err) { setError(formatApiError(err)); }
        finally { setSilencing(null); }
    };

    const unsilence = async (sig) => {
        setSilencing(sig); setOk(""); setError("");
        try {
            await api.delete(`/errors/signatures/${sig}/silence`);
            setOk(`Firma ${sig} reactivada.`);
        } catch (err) { setError(formatApiError(err)); }
        finally { setSilencing(null); }
    };

    return (
        <div data-testid="error-logs-page">
            <div className="page-head">
                <div>
                    <h1>Bitácora de errores frontend</h1>
                    <p>Reportes recibidos por el ErrorBoundary. Silencia una firma cuando ya tengas el bug identificado y estés trabajando en él.</p>
                </div>
                <button className="btn btn-ghost" onClick={load} data-testid="elog-refresh"><RefreshCw size={14} /> Recargar</button>
            </div>

            {error && <div className="alert alert-error" data-testid="elog-error">{error}</div>}
            {ok && <div className="alert alert-ok" data-testid="elog-ok">{ok}</div>}

            <div className="card card-padded" style={{ marginBottom: 12 }}>
                <div className="flex gap-2" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div className="field" style={{ minWidth: 200 }}>
                        <label className="text-xs"><Filter size={11} style={{ verticalAlign: "-1px" }} /> Ventana</label>
                        <select className="select" value={filters.since_hours} onChange={(e) => { setFilters({ ...filters, since_hours: Number(e.target.value) }); setPage(0); }} data-testid="elog-filter-since">
                            {TIME_WINDOWS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
                        </select>
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="text-xs">Firma</label>
                        <input className="input mono" value={filters.signature} onChange={(e) => { setFilters({ ...filters, signature: e.target.value.trim() }); setPage(0); }} placeholder="ej. 33f506cd1b8c7698" data-testid="elog-filter-signature" />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="text-xs">user_id</label>
                        <input className="input mono" value={filters.user_id} onChange={(e) => { setFilters({ ...filters, user_id: e.target.value.trim() }); setPage(0); }} placeholder="ej. user_xxx" data-testid="elog-filter-user" />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="text-xs">Buscar en mensaje/URL</label>
                        <input className="input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="texto a buscar" data-testid="elog-filter-q" />
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}><AlertOctagon size={14} /> Resultados ({total} en ventana, mostrando {filtered.length})</h3>
                </div>
                {loading && <div style={{ padding: 30, textAlign: "center" }}><span className="loader" /></div>}
                {!loading && filtered.length === 0 && (
                    <div className="text-muted" style={{ textAlign: "center", padding: 30 }}>✓ Sin reportes en este filtro.</div>
                )}
                {!loading && filtered.length > 0 && (
                    <div className="table-wrap">
                        <table className="t">
                            <thead>
                                <tr>
                                    <th style={{ width: 130 }}>Fecha</th>
                                    <th style={{ width: 130 }}>Firma</th>
                                    <th>Mensaje</th>
                                    <th style={{ width: 180 }}>Usuario</th>
                                    <th style={{ width: 110, textAlign: "right" }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => (
                                    <React.Fragment key={r.report_id}>
                                        <tr data-testid={`elog-row-${r.report_id}`} onClick={() => setExpanded(expanded === r.report_id ? null : r.report_id)} style={{ cursor: "pointer" }}>
                                            <td className="mono text-xs text-muted">{new Date(r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" })}</td>
                                            <td className="mono text-xs">{r.signature}</td>
                                            <td className="mono text-xs" style={{ color: "#F87171", maxWidth: 480, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.message}>{r.message}</td>
                                            <td className="text-sm">{r.user ? <><div>{r.user.name}</div><div className="text-muted mono text-xs">{r.user.email}</div></> : <span className="text-muted">anónimo</span>}</td>
                                            <td style={{ textAlign: "right" }}>
                                                <button
                                                    className="btn btn-ghost btn-sm"
                                                    onClick={(e) => { e.stopPropagation(); silence(r.signature); }}
                                                    disabled={silencing === r.signature}
                                                    data-testid={`elog-silence-${r.signature}`}
                                                    title="Silenciar alertas para esta firma 24h"
                                                >
                                                    <BellOff size={12} /> 24h
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded === r.report_id && (
                                            <tr data-testid={`elog-detail-${r.report_id}`}>
                                                <td colSpan={5} style={{ background: "var(--bg-2)" }}>
                                                    <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                                        <div>
                                                            <div className="text-muted text-xs mono" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>URL</div>
                                                            <div className="mono text-xs" style={{ wordBreak: "break-all" }}>{r.url || "—"}</div>
                                                            <div className="text-muted text-xs mono" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginTop: 10, marginBottom: 4 }}>Navegador</div>
                                                            <div className="text-xs" style={{ wordBreak: "break-word" }}>{r.user_agent || "—"}</div>
                                                            <div className="text-muted text-xs mono" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginTop: 10, marginBottom: 4 }}>IP</div>
                                                            <div className="mono text-xs">{r.client_ip || "—"}</div>
                                                            <button className="btn btn-secondary btn-sm" style={{ marginTop: 14 }} onClick={() => unsilence(r.signature)} disabled={silencing === r.signature} data-testid={`elog-unsilence-${r.signature}`}>
                                                                <BellRing size={12} /> Quitar silencio de esta firma
                                                            </button>
                                                        </div>
                                                        <div>
                                                            <div className="text-muted text-xs mono" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Stack</div>
                                                            <pre style={{ background: "#0F0F14", color: "#F87171", padding: 10, fontSize: 11, borderRadius: 6, maxHeight: 220, overflow: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.stack || "—"}</pre>
                                                            {r.component_stack && (
                                                                <>
                                                                    <div className="text-muted text-xs mono" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginTop: 10, marginBottom: 4 }}>Component stack</div>
                                                                    <pre style={{ background: "#0F0F14", color: "#A3A3A8", padding: 10, fontSize: 11, borderRadius: 6, maxHeight: 160, overflow: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.component_stack}</pre>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {total > PAGE_SIZE && (
                    <div className="flex gap-2 items-center" style={{ padding: 12, justifyContent: "center" }}>
                        <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="elog-prev">← Anterior</button>
                        <span className="text-muted text-xs">Página {page + 1} de {Math.ceil(total / PAGE_SIZE)}</span>
                        <button className="btn btn-ghost btn-sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)} data-testid="elog-next">Siguiente →</button>
                    </div>
                )}
            </div>
        </div>
    );
}
